import { numberFormat, unitToString } from '@/utils/unitUtils';
import get from 'just-safe-get';

// Constants
export const COLOR_PALETTES = {
  emissions: [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-red-500',
    'bg-indigo-500',
    'bg-pink-500',
    'bg-yellow-500',
    'bg-teal-500',
    'bg-cyan-500',
  ],
  costs: [
    'bg-orange-500',
    'bg-orange-400',
    'bg-indigo-500',
    'bg-indigo-400',
    'bg-pink-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-red-500',
    'bg-yellow-500',
    'bg-teal-500',
    'bg-cyan-500',
  ],
};

export const normalize = (data, labelPath, valuePath) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map(item => ({
    label: get(item, labelPath),
    value: get(item, valuePath),
  }));
};

export const calculateScale = values => {
  const positiveSum = values.filter(v => v >= 0).reduce((sum, v) => sum + v, 0);
  const negativeSum = Math.abs(values.filter(v => v < 0).reduce((sum, v) => sum + v, 0));

  return {
    scaleMin: negativeSum === 0 ? 0 : -negativeSum,
    scaleMax: positiveSum,
  };
};

export const prepareChartData = data => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return {
      scaleMin: 0,
      scaleMax: 1,
      hasNegativeValues: false,
    };
  }

  const values = data.map(item => item.value);
  const hasNegativeValues = values.some(v => v < 0);
  const { scaleMin, scaleMax } = calculateScale(values);

  return { scaleMin, scaleMax, hasNegativeValues };
};

export const createTick = (value, label, scaleMin, range, isZero = false) => ({
  value,
  label,
  position: ((value - scaleMin) / range) * 100,
  ...(isZero && { isZero: true }),
});

export const createTicks = (scaleMin, scaleMax) => {
  const range = scaleMax - scaleMin;
  const ticks = [];

  // Always include min and max
  ticks.push(createTick(scaleMin, numberFormat(scaleMin, 2), scaleMin, range));
  ticks.push(createTick(scaleMax, numberFormat(scaleMax, 2), scaleMin, range));

  // Add zero if it's in range
  if (scaleMin < 0 && scaleMax > 0) {
    ticks.push(createTick(0, '0', scaleMin, range, true));
  }

  // Add intermediate ticks if there's enough space
  const zeroPosition = Math.abs(scaleMin) / range;

  if (zeroPosition > 0.25) {
    const midBeforeZero = scaleMin + Math.abs(scaleMin) / 2;
    ticks.push(createTick(midBeforeZero, numberFormat(midBeforeZero, 2), scaleMin, range));
  }

  if (zeroPosition < 0.75) {
    const midAfterZero = scaleMax / 2;
    ticks.push(createTick(midAfterZero, numberFormat(midAfterZero, 2), scaleMin, range));
  }

  return ticks.sort((a, b) => a.value - b.value);
};

export const calculateBarPositions = (items, totalRange, zeroLinePosition) => {
  const negativeItems = items.filter(item => item.value < 0);
  const positiveItems = items.filter(item => item.value >= 0);
  const positions = [];

  // Position negative items (left side)
  negativeItems.forEach((item, i) => {
    const width = (Math.abs(item.value) / totalRange) * 100;
    const left = negativeItems
      .slice(0, i)
      .reduce((acc, dataItem) => acc + (Math.abs(dataItem.value) / totalRange) * 100, 0);
    positions.push({ ...item, width, left, isNegative: true });
  });

  // Position positive items (right side)
  positiveItems.forEach((item, i) => {
    const width = (item.value / totalRange) * 100;
    const left =
      zeroLinePosition +
      positiveItems.slice(0, i).reduce((acc, dataItem) => acc + (dataItem.value / totalRange) * 100, 0);
    positions.push({ ...item, width, left, isNegative: false });
  });

  return positions;
};

export const getEmissionsUnit = emissionsData => {
  const { emissions, levelization } = emissionsData;
  const unit = unitToString(emissions[0]?.quantity.unit);
  const label = 'CO₂e';
  return `${unit}${label}/${unitToString(levelization.unit)}`;
};

export const getCostsUnit = costsData => {
  const { levelization } = costsData;
  const label = '$';
  return `${label}/${unitToString(levelization.unit)}`;
};

export const getUnitAggregated = data => {
  const { unit } = data;
  return `${unitToString(unit)}`;
};
